
/** A single image that needs to be loaded */
export type ImagePrefetchImage = {
  /** Unique shorthand */
  key: string,
  /** The exact URL of the image */
  url: string,
}

/** A sequentiallly ordered list of images that need to be loaded */
export type ImagePrefetchImages = ImagePrefetchImage[]

/** 
 * A map of image keys to their loading state. If a key is missing then 
 * no attempt has been made to load the image yet. If it is set to false 
 * then the image is currently loading. If it is set to true then the image 
 * has been loaded successfully.
 */
export type ImagePrefetchLoaded = { [key: string]: boolean }

/** 
 * A mutable number that can be updated and read from multiple places
 */
export type MutableNumberArg = {
  current: number
}

/**
 * React-style setter that accepts either a value or a function that takes the previous value
 */
type SetStateAction<T> = T | ((prev: T) => T)
type Dispatch<T> = (value: SetStateAction<T>) => void

/**
 * Props for the imagePrefetch function
 */
export type ImagePrefetchProps = {
  /** When the iterator changes it signals an interrupt and we need to bail out  */
  iteration: MutableNumberArg
  /** Shared counter for how many images are currently loading across all prefetch instances */
  activelyLoading: MutableNumberArg
  /** Where to start this iteration of the prefetch process */
  startAtIndex: number
  /** Whether to prefetch in forward or reverse order */
  reverse: boolean
  /** 1 or more images can be loaded at the same time */
  simultaneous: number
  /** 
   * How many images to look ahead at full speed before shifting into 
   * low gear and slowing down. -1 means infinity.
   */
  lookAhead: number
  /** The ordered sequence of images that need to be prefetched */
  images: ImagePrefetchImages
  /** The current state of which images are loading or loaded */
  loaded: ImagePrefetchLoaded
  setLoaded: Dispatch<ImagePrefetchLoaded>
  /** 
   * This function may signal to the caller whether it is currently running 
   * in "high gear" (full speed with no delays) or "low gear" (slowed down 
   * to free up bandwiddth)
   */
  setHighGear: Dispatch<boolean>
}

/**
 * Prefetch some or all of the images in the sequence in forward or reverse 
 * order. The starting point could be anywhere in the total sequence.
 */
export async function imagePrefetch({
    iteration,
    activelyLoading,
    startAtIndex,
    reverse,
    simultaneous,
    lookAhead,
    images,
    loaded,
    setLoaded,
    setHighGear,
  }: ImagePrefetchProps) {

  // Capture the iteration value at the start - if this changes, we should bail
  const myIteration = iteration.current
  
  // Make a local copy of loaded state to work with (avoids stale state issues)
  let localLoaded: ImagePrefetchLoaded = { ...loaded }
  
  // Helper to check if we should bail out (iteration changed)
  const shouldBailOut = () => iteration.current !== myIteration
  
  // Helper to update both local copy and React state for an image's loaded state
  const updateLoaded = (key: string, value: boolean) => {
    localLoaded[key] = value
    setLoaded(prev => ({ ...prev, [key]: value }))
  }
  
  // Decrement the actively loading count on load success or error
  const resolveLoad = (key: string) => {
    activelyLoading.current--
    setLoaded(prev => ({ ...prev, [key]: true }))
  }
  
  // Helper to start loading a single image
  const loadOneImage = (image: ImagePrefetchImage): boolean => {
    // Check if already loaded or currently loading
    if (localLoaded[image.key] !== undefined) return false
    
    // Mark as loading (update both local copy and state)
    activelyLoading.current++
    updateLoaded(image.key, false)
    
    // Create and load image
    const img = new Image()
    img.onload = () => resolveLoad(image.key)
    img.onerror = () => resolveLoad(image.key)
    
    img.src = image.url
    return true
  }
  
  let currentOffset = 0
  const LOW_GEAR_BASE_DELAY = 10 // Base delay in low gear (ms per offset past lookAhead)
 
  // Start in high gear (full speed)
  let lastHighGearState = true // Track previous gear state to avoid unnecessary updates
  // Reinforce the high gear signal
  setHighGear(true)
  
  // Main prefetch loop. We continue until we've exhausted the sequence or 
  // the iteration changes.
  while (true) {
    // Check if we should bail
    if (shouldBailOut()) break
    
    // Determine if we should be in high or low gear based on how far ahead we are
    // If lookAhead === -1, treat it as infinity (always high gear)
    const nowHighGear = lookAhead === -1 || currentOffset < lookAhead
    // Only update state if it actually changed to prevent thrashing
    if (lastHighGearState !== nowHighGear) {
      setHighGear(nowHighGear)
      lastHighGearState = nowHighGear
    }
    
    // If we have room for more concurrent loads, try to start one
    if (activelyLoading.current < simultaneous) {
      if (shouldBailOut()) break

      // Look ahead from startAtIndex in the specified direction
      const offset = reverse ? -currentOffset : currentOffset
      const targetIndex = startAtIndex + offset

      // Check bounds
      if (targetIndex < 0 || targetIndex >= images.length) break

      const image = images[targetIndex]
      if (image) {
        // Try to start loading this image
        if (loadOneImage(image)) {
          // If we're in low gear, add a progressive delay before continuing
          // Delay increases by 10ms for each offset past lookAhead
          if (!nowHighGear && lookAhead !== -1) {
            const delay = LOW_GEAR_BASE_DELAY * (currentOffset - lookAhead)
            await sleep(delay)
          }
        }
      }
      currentOffset++
      continue // Continue progressing forward in this loop
    }
    
    // If we're at capacity, wait a bit for some loads to complete, then check again
    if (activelyLoading.current > 0) {
      await sleep(10)
      continue
    }
  }

  if (!shouldBailOut()) {
    // Signal that we're done loading (low gear)
    setHighGear(false)
  }
}

// Helper to sleep for a specified number of milliseconds
async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
